import React, { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import { showToast } from "components/ui/toast";
import Button from "components/ui/Button";
import ProductCategoriesSection from "./ProductCategoriesSection";
import AudienceSelect, {
  toAudienceValue,
  fromAudienceValue,
  validateAudience,
} from "components/products/AudienceSelect";
import { useUploadFiles } from "apis/upload";
import { MdOutlineClose } from "react-icons/md";

const ShopProductEdit = ({ product, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "shop",
    category: "payment",
    price: "",
    calendlyUrl: "",
    description: "",
    // hasMultipleCategories: when category=payment, toggles between a single flat
    // price (false) and multiple categories each with their own quantity rows (true)
    hasMultipleCategories: false,
    enableOrderNotes: false,
  });

  // Holds the product categories — only used when category=payment AND hasMultipleCategories=true
  const [productCategories, setProductCategories] = useState([]);

  // Quantity row errors keyed as "categoryId-rowIndex-fieldName"
  const [quantityErrors, setQuantityErrors] = useState({});

  const [files, setFiles] = useState({ thumbnail: null, video: null });
  const [previews, setPreviews] = useState({ thumbnail: null, video: null });
  const [errors, setErrors] = useState({});
  const [notifyEmails, setNotifyEmails] = useState([]);
  const [notifyEmailInput, setNotifyEmailInput] = useState("");
  const [audience, setAudience] = useState({ mode: "all", offices: [] });

  const { uploadSingle } = useUploadFiles();

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || "",
        category: product.category || "payment",
        price: product.price || "",
        calendlyUrl: product.calendlyUrl || "",
        description: product.description || "",
        id: product._id || "",
        hasMultipleCategories: product.hasMultipleCategories || false,
        enableOrderNotes: product.enableOrderNotes || false,
      });
      setPreviews({
        thumbnail: product.thumbnail || null,
        video: product.video || null,
      });
      setAudience(toAudienceValue(product.offices));
      try {
        let initialNotifyEmails = [];
        if (product.notifyEmails) {
          if (Array.isArray(product.notifyEmails)) initialNotifyEmails = product.notifyEmails;
          else initialNotifyEmails = JSON.parse(product.notifyEmails || "[]");
        }
        setNotifyEmails(Array.isArray(initialNotifyEmails) ? initialNotifyEmails : []);
      } catch (e) {
        setNotifyEmails([]);
      }

      if (product.hasMultipleCategories && Array.isArray(product.categories)) {
        const normalized = product.categories.map((cat) => ({
          _id: cat._id,
          name: cat.name,
          description: cat.description,
          quantities: cat.quantities.map((q) => ({
            _id: q._id,
            label: q.label,
            quantity: q.quantity,
            price: q.price,
            totalPrice: q.totalPrice,
          })),
          images: cat.images || [], // pre-populate from saved URLs
        }));
        setProductCategories(normalized);
      }
    }
  }, [product]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "hasMultipleCategories") {
      setProductCategories([]);
      setQuantityErrors({});
      setFormData((prev) => ({
        ...prev,
        hasMultipleCategories: checked,
        price: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (fileType === "thumbnail") {
        const validTypes = [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "image/webp",
        ];
        if (!validTypes.includes(file.type)) {
          showToast(
            "error",
            "Please upload a valid image file (JPG, PNG, WEBP)",
          );
          return;
        }
        if (file.size > 15 * 1024 * 1024) {
          showToast("error", "Thumbnail size should be less than 5MB");
          return;
        }
      } else if (fileType === "video") {
        const validTypes = [
          "video/mp4",
          "video/mov",
          "video/avi",
          "video/quicktime",
        ];
        if (!validTypes.includes(file.type)) {
          showToast(
            "error",
            "Please upload a valid video file (MP4, MOV, AVI)",
          );
          return;
        }
        if (file.size > 100 * 1024 * 1024) {
          showToast("error", "Video size should be less than 100MB");
          return;
        }
      }

      setFiles((prev) => ({ ...prev, [fileType]: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews((prev) => ({ ...prev, [fileType]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (fileType) => {
    setFiles((prev) => ({ ...prev, [fileType]: null }));
    setPreviews((prev) => ({
      ...prev,
      [fileType]: product ? product[fileType] : null,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    const audienceError = validateAudience(audience);
    if (audienceError) {
      newErrors.audience = audienceError;
    }

    if (formData.category === "payment") {
      if (!formData.hasMultipleCategories) {
        // Flat price — must be provided and positive
        if (!formData.price || formData.price <= 0) {
          newErrors.price = "Price is required and must be greater than 0";
        }
      } else {
        // Multiple categories — must have at least one
        if (productCategories.length === 0) {
          newErrors.productCategories = "At least one category is required";
        }

        const newQuantityErrors = {};
        productCategories.forEach((category, catIndex) => {
          category.quantities.forEach((row, index) => {
            if (!row.label.trim()) {
              newQuantityErrors[`${catIndex}-${index}-label`] =
                "Label is required";
            }
            if (
              row.quantity === "" ||
              row.quantity === null ||
              row.quantity === undefined
            ) {
              newQuantityErrors[`${catIndex}-${index}-quantity`] =
                "Quantity is required";
            } else if (parseFloat(row.quantity) < 0) {
              newQuantityErrors[`${catIndex}-${index}-quantity`] =
                "Quantity must be 0 or greater";
            }
            if (
              row.price === "" ||
              row.price === null ||
              row.price === undefined
            ) {
              newQuantityErrors[`${catIndex}-${index}-price`] =
                "Price is required";
            } else if (parseFloat(row.price) < 0) {
              newQuantityErrors[`${catIndex}-${index}-price`] =
                "Price must be 0 or greater";
            }
          });
        });

        setQuantityErrors(newQuantityErrors);
        if (Object.keys(newQuantityErrors).length > 0) {
          newErrors.quantities = "Please fix quantity errors before submitting";
        }
      }
    }

    if (formData.category === "calendly") {
      if (!formData.calendlyUrl.trim()) {
        newErrors.calendlyUrl = "Calendly URL is required";
      } else if (!formData.calendlyUrl.includes("calendly.com")) {
        newErrors.calendlyUrl = "Please enter a valid Calendly URL";
      }
    }

    if (!product && !files.thumbnail) {
      newErrors.thumbnail = "Thumbnail is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addNotifyEmail = () => {
    const email = (notifyEmailInput || "").trim().toLowerCase();
    if (!email) return;
    const emails = email
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e && !notifyEmails.includes(e));
    if (emails.length) setNotifyEmails((prev) => [...prev, ...emails]);
    setNotifyEmailInput("");
  };

  const removeNotifyEmail = (index) => {
    setNotifyEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("category", formData.category);
    submitData.append("description", formData.description);
    submitData.append("hasMultipleCategories", formData.hasMultipleCategories);
    // Empty = offered to all offices.
    submitData.append("offices", JSON.stringify(fromAudienceValue(audience)));

    if (formData.category === "payment") {
      if (!formData.hasMultipleCategories) {
        submitData.append("price", formData.price);
      } else {
        // Upload new images for each categories to S3 first before submitting
        const categoriesWithUploadedImages = await Promise.all(
          productCategories.map(async (category) => {
            const newImages = (category.images || []).filter(
              (img) => typeof img === "object",
            );
            const existingImages = (category.images || []).filter(
              (img) => typeof img === "string",
            );

            // Upload all new images in parallel
            const uploadedUrls = await Promise.all(
              newImages.map(({ file }) =>
                uploadSingle({ file, folder: "shop/categories" }).then(
                  (results) => results[0]?.url,
                ),
              ),
            );

            return {
              ...category,
              images: [...existingImages, ...uploadedUrls.filter(Boolean)],
            };
          }),
        );

        submitData.append(
          "categories",
          JSON.stringify(categoriesWithUploadedImages),
        );
      }
    }

    if (formData.category === "calendly") {
      submitData.append("calendlyUrl", formData.calendlyUrl);
    }

    if (files.thumbnail) submitData.append("thumbnail", files.thumbnail);
    if (files.video) submitData.append("video", files.video);

    submitData.append("type", "shop");
    submitData.append("enableOrderNotes", formData.enableOrderNotes ? "true" : "false");
    if (notifyEmails && notifyEmails.length > 0) {
      submitData.append("notifyEmails", JSON.stringify(notifyEmails));
    }
    onSubmit(submitData);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      className="max-w-xl"
      title={product ? "Edit Shop Product" : "Add Shop Product"}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Label */}
        <div>
          <label className="form-label">
            Label <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`form-input border ${
              errors.name ? "border-red-500" : "border-mediumgray"
            }`}
            placeholder="Enter product name"
          />
          {errors.name && (
            <p className="form-error-message mt-1">{errors.name}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="form-label">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="form-select border border-mediumgray"
          >
            <option value="payment">Payment</option>
            <option value="calendly">Calendly</option>
            <option value="video">Video</option>
          </select>
        </div>

        {/* Payment Fields */}
        {formData.category === "payment" && (
          <>
            {/* hasMultipleCategories toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="hasMultipleCategories"
                name="hasMultipleCategories"
                checked={formData.hasMultipleCategories}
                onChange={handleInputChange}
                className="w-4 h-4 cursor-pointer"
              />
              <label
                htmlFor="hasMultipleCategories"
                className="form-label mb-0 cursor-pointer"
              >
                This product has multiple categories
              </label>
            </div>

            {/* Flat price — shown when hasMultipleCategories is false */}
            {!formData.hasMultipleCategories && (
              <div>
                <label className="form-label">
                  Price (AUD) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className={`form-input border ${
                    errors.price ? "border-red-500" : "border-mediumgray"
                  }`}
                  placeholder="0.00"
                />
                {errors.price && (
                  <p className="form-error-message mt-1">{errors.price}</p>
                )}
              </div>
            )}

            {/* Multiple categories — shown when hasMultipleCategories is true */}
            {formData.hasMultipleCategories && (
              <>
                <ProductCategoriesSection
                  productCategories={productCategories}
                  setProductCategories={setProductCategories}
                  quantityErrors={quantityErrors}
                  setQuantityErrors={setQuantityErrors}
                />
                {errors.productCategories && (
                  <p className="form-error-message mt-1">
                    {errors.productCategories}
                  </p>
                )}
              </>
            )}
          </>
        )}

        {/* Calendly Fields */}
        {formData.category === "calendly" && (
          <div>
            <label className="form-label">
              Calendly URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              name="calendlyUrl"
              value={formData.calendlyUrl}
              onChange={handleInputChange}
              className={`form-input border ${
                errors.calendlyUrl ? "border-red-500" : "border-mediumgray"
              }`}
              placeholder="https://calendly.com/your-link"
            />
            {errors.calendlyUrl && (
              <p className="form-error-message mt-1">{errors.calendlyUrl}</p>
            )}
          </div>
        )}

        {/* Description */}
        <div>
          <label className="form-label">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            className={`form-textarea border ${
              errors.description ? "border-red-500" : "border-mediumgray"
            }`}
            placeholder="Enter product description"
          />
          {errors.description && (
            <p className="form-error-message mt-1">{errors.description}</p>
          )}
        </div>

        {/* Available to (office audience) */}
        <div>
          <label className="form-label">Available To</label>
          <AudienceSelect
            value={audience}
            onChange={setAudience}
            error={errors.audience}
          />
        </div>

        {/* Enable Order Notes */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="enableOrderNotes"
            name="enableOrderNotes"
            checked={formData.enableOrderNotes}
            onChange={handleInputChange}
            className="w-4 h-4 cursor-pointer"
          />
          <div>
            <label htmlFor="enableOrderNotes" className="font-medium cursor-pointer">
              Enable Order Notes
            </label>
            <p className="text-sm text-darkergray">
              Allow customers to add notes when purchasing this product.
            </p>
          </div>
        </div>

        {/* Notify Emails on Purchase */}
        <div className="space-y-2">
          <label className="form-label">Notify These Emails on Purchase</label>
          <p className="text-sm text-darkergray -mt-1 mb-2">
            Admin-only. These addresses receive a notification each time this product is purchased.
          </p>

          {/* Tag chips */}
          {notifyEmails.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {notifyEmails.map((email, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1 px-3 py-1 bg-lightgray text-darkergray rounded-full text-sm"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeNotifyEmail(i)}
                    className="ml-1 text-darkgray hover:text-black leading-none"
                    aria-label={`Remove ${email}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input row */}
          <div className="flex gap-2">
            <input
              type="email"
              value={notifyEmailInput}
              onChange={(e) => setNotifyEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addNotifyEmail();
                }
              }}
              placeholder="email@example.com — press Enter or comma to add"
              className="form-input flex-1 border border-mediumgray"
            />
            <button
              type="button"
              onClick={addNotifyEmail}
              className="gray-button whitespace-nowrap"
            >
              Add
            </button>
          </div>
        </div>

        {/* Thumbnail Upload */}
        <div>
          <label className="form-label">
            Thumbnail {!product && <span className="text-red-500">*</span>}
          </label>
          <div className="space-y-3">
            {previews.thumbnail && (
              <div className="relative inline-block">
                <img
                  src={previews.thumbnail}
                  alt="Thumbnail preview"
                  className="w-full max-w-md h-48 object-cover rounded-lg border border-mediumgray"
                />
                <button
                  type="button"
                  onClick={() => removeFile("thumbnail")}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <MdOutlineClose size={18} />
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg,image/webp"
              onChange={(e) => handleFileChange(e, "thumbnail")}
              className="form-input border border-mediumgray"
            />
            <p className="text-xs text-darkgray">
              Accepted formats: JPG, PNG, WEBP. Max size: 5MB
            </p>
          </div>
          {errors.thumbnail && (
            <p className="form-error-message mt-1">{errors.thumbnail}</p>
          )}
        </div>

        {/* Video Upload */}
        <div>
          <label className="form-label">Video (Optional)</label>
          <div className="space-y-3">
            {previews.video && (
              <div className="relative inline-block w-full">
                <video
                  src={previews.video}
                  controls
                  className="w-full max-w-md h-48 object-cover rounded-lg border border-mediumgray"
                />
                <button
                  type="button"
                  onClick={() => removeFile("video")}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <MdOutlineClose size={18} />
                </button>
              </div>
            )}
            <input
              type="file"
              accept="video/mp4,video/mov,video/avi,video/quicktime"
              onChange={(e) => handleFileChange(e, "video")}
              className="form-input border border-mediumgray"
            />
            <p className="text-xs text-darkgray">
              Accepted formats: MP4, MOV, AVI. Max size: 100MB
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-mediumgray">
          <Button
            type="button"
            onClick={onClose}
            className="outline-button"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="black-button"
            disabled={isLoading}
            loading={isLoading}
          >
            {product ? "Update Product" : "Create Product"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ShopProductEdit;
